import Vue from 'vue';

// Preload
import './plugins/toLocalIsoString'; // add LocalIsoString method to Date object
import './registerServiceWorker';
import './router/moduleRoutes'; // add autoimporter for all routes defined within ./modules/*/routes

// Plugins
import Vuelidate from 'vuelidate';
import { Promised } from 'vue-promised';
import PortalVue from 'portal-vue';
import ActionCableVue from 'actioncable-vue';

// Customized Plugins
import vuetify from './plugins/vuetify';
import { i18n, loadLanguageAsync } from './plugins/vue-i18n';
const validations = () =>
  import(/* webpackChunkName: "validations" */ './validations');
import store from './store';
import router from './router';
// import api from './services/apiService';

// Base components
import './components/base/_global';
import './components/forms/inputs/_global';
import App from './App.vue';

// Directives
import ContextOutside from './directives/ContextOutside';

// Constants and import requirements for root
import {
  translateValidator,
  translateValidationErrors,
} from './functions/translationHelpers';
import DATE_LOCALE from './i18n/date-fns-locale';
import { isLightColor } from '@/functions/isLightColor';
import { getColorArrayModNumber, mapValues } from '@/functions/utils';

// Install and configure simple plugins
Vue.component('Promised', Promised);

Vue.use(Vuelidate);
Vue.use(PortalVue);
Vue.use(ActionCableVue, {
  connectImmediately: false,
});

// Register custom directives
Vue.directive('context-outside', ContextOutside);

// Add hooks to Vue class
Vue.prototype.$locale = loadLanguageAsync; // function hook into codesplitted loading of translations
Vue.prototype.$log =
  process.env.NODE_ENV === 'development' ? console.log : () => {}; // Alias to easily access console.log in vue sfc html template, disabled in production
Vue.prototype.$tve = translateValidationErrors;
Vue.prototype.$validations = mapValues(
  validations,
  (validator, validatorName) => v =>
    validator(v) || translateValidator(validatorName)
); // LEGACY: Support vuetify form validations, with returning

Vue.config.productionTip = false;

// Generate app only after load of page (avoid loading layouts when css is not fully loaded yet) smoother UX
window.addEventListener('load', () => {
  const app = new Vue({
    router,
    store,
    i18n,
    vuetify,
    computed: {
      activeTheme: {
        get() {
          return (
            this?.$vuetify?.theme?.themes[
              this.$vuetify.theme.dark ? 'dark' : 'light'
            ] || {}
          );
        },
        set(v) {
          if (!this?.$vuetify?.theme?.themes) return;

          const oldTheme = this.$vuetify.theme.themes[
            this.$vuetify.theme.dark ? 'dark' : 'light'
          ];

          this.$vuetify.theme.themes[
            this.$vuetify.theme.dark ? 'dark' : 'light'
          ] = { ...oldTheme, ...v }; // give back new object to make sure reactivity triggers
        },
      },
      theme() {
        // this.$root.theme
        const { activeTheme } = this;

        // Automatic recalculation, mutates object to avoid infinite reactivity loop
        activeTheme.onBranding = isLightColor(activeTheme.branding)
          ? '#000'
          : '#FFF';

        /* DASHBOARD TILES COLOURS */
        const colorWheelDashboardTiles = [
          '#976FA3',
          '#E5AA46',
          '#60B0BF',
          '#EA810E',
          '#715699',
          '#60AAA9',
          '#489CC1',
          '#5DA55C',
          '#4A7CB9',
          '#607D8B',
          '#035C84',
        ];

        const getColorWheelByNumber = n =>
          getColorArrayModNumber(colorWheelDashboardTiles, n);

        return {
          ...activeTheme,
          colorWheelDashboardTiles,
          getColorWheelByNumber,
        };
      },
      dateLocale() {
        return { locale: DATE_LOCALE[this.$i18n.locale] }; // REFACTOR: don't import everything, use dynamic import statement with named exports
      },
      debugVuetify() {
        // show $vuetify in dev tools, disable for production
        return process.env.NODE_ENV === 'development' && this.$vuetify;
      },
    },
    render: h => h(App),
  }).$mount('#app');

  store.$app = app; // access rendering Vue instance with $app in plugins
  router.$app = app; // access rendering Vue instance with $app in plugins
});

//Postload
window.addEventListener('beforeinstallprompt', event => {
  event.preventDefault();
  store.set('pwaInstallEvent', event); //vuex-pathify set
});
